/* Cart milestone widget — keeps the unlocked state, the earned coupon and the
   "Add ₹X more" nudge in step with the cart, and runs the Copy code button.

   Loaded from layout/theme.liquid rather than inlined in the snippet: the cart
   drawer re-renders through innerHTML, and innerHTML does not execute script
   tags, so an inline script would run once and then go dead the first time the
   customer changed a quantity. This is the same reason maahi-cart-recs.js is a
   separate asset.

   PRESENTATIONAL ONLY. It reads /cart.js, rewrites text and classes, and writes
   a coupon code to the clipboard. It never posts to the cart, never touches the
   Razorpay button, and never runs on any page that has no widget on it. Every
   handler is wrapped so a failure here can only leave the widget's
   server-rendered state showing — it cannot throw into the cart or checkout.

   Every threshold, string and code comes from data attributes written by
   snippets/maahi-cart-milestones.liquid, so the copy and the codes live in one
   place — change them there, not here. */
(function () {
  'use strict';

  function attr(root, name) {
    return root.getAttribute(name) || '';
  }

  /* ₹ amounts in Indian grouping, rupees rounded UP so "Add ₹X more" is never
     a rupee short of the threshold. */
  function rupees(paise) {
    var r = Math.ceil(paise / 100);
    try {
      return r.toLocaleString('en-IN');
    } catch (e) {
      return String(r);
    }
  }

  function apply(root, totalPaise) {
    var t2 = parseInt(attr(root, 'data-ms-t2'), 10);
    var t3 = parseInt(attr(root, 'data-ms-t3'), 10);
    if (isNaN(t2) || isNaN(t3)) return;

    var tier = totalPaise >= t3 ? 3 : totalPaise >= t2 ? 2 : 1;

    var statusEl = root.querySelector('[data-ms-status]');
    var statusText = attr(root, 'data-ms-status-' + tier);
    if (statusEl && statusText) statusEl.textContent = statusText;

    /* Step 1 is never touched — the sale price has no threshold. */
    var step2 = root.querySelector('[data-ms-step="2"]');
    var step3 = root.querySelector('[data-ms-step="3"]');
    if (step2) step2.classList.toggle('is-done', tier >= 2);
    if (step3) step3.classList.toggle('is-done', tier === 3);

    /* Connector fill: 50% once dot 2 is reached, 100% at dot 3. */
    var fill = root.querySelector('[data-ms-fill]');
    if (fill) {
      fill.classList.toggle('is-t2', tier === 2);
      fill.classList.toggle('is-t3', tier === 3);
    }

    /* The earned coupon — one per order, so only the best code shows. */
    var coupon = root.querySelector('[data-ms-coupon]');
    if (coupon) {
      if (tier === 1) {
        coupon.hidden = true;
      } else {
        var code = attr(root, 'data-ms-code-' + tier);
        var off = attr(root, 'data-ms-off-' + tier);
        var codeEl = coupon.querySelector('[data-ms-code]');
        var changed = codeEl && codeEl.textContent !== code;
        if (codeEl && code) codeEl.textContent = code;
        coupon.querySelectorAll('[data-ms-off]').forEach(function (el) {
          if (off) el.textContent = off;
        });
        var btn = coupon.querySelector('[data-ms-copy]');
        if (btn && code) btn.setAttribute('aria-label', 'Copy coupon code ' + code);
        if (changed) resetCopied(btn);
        /* Un-hiding restarts the CSS reveal; a swapped code replays it too. */
        if (changed && !coupon.hidden) {
          coupon.classList.remove('is-replay');
          void coupon.offsetWidth;
          coupon.classList.add('is-replay');
        }
        coupon.hidden = false;
      }
    }

    var nudgeEl = root.querySelector('[data-ms-nudge]');
    var nudge = attr(root, 'data-ms-nudge-' + tier);
    if (nudgeEl && nudge) {
      var gap = tier === 1 ? t2 - totalPaise : tier === 2 ? t3 - totalPaise : 0;
      nudgeEl.textContent = nudge.replace('{amt}', rupees(gap));
    }
  }

  function refresh() {
    var root = document.querySelector('[data-maahi-milestones]');
    if (!root) return;

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        /* total_price is paise and is the pre-coupon total, which is what the
           Razorpay minimum-spend rules are written against. */
        if (!cart || typeof cart.total_price !== 'number') return;
        var current = document.querySelector('[data-maahi-milestones]');
        if (current) apply(current, cart.total_price);
      })
      .catch(function () {
        /* Leave the server-rendered state alone. It was correct when the page
           was built, so a failed refresh shows slightly stale copy at worst —
           never a wrong or broken cart. */
      });
  }

  /* ── Copy code ───────────────────────────────────────────────────────── */

  var resetTimer;

  function resetCopied(btn) {
    if (!btn) return;
    btn.classList.remove('is-copied');
    var label = btn.querySelector('[data-ms-copy-label]');
    if (label) label.textContent = 'Copy code';
  }

  /* Fallback for browsers without the async clipboard. The textarea goes
     INSIDE the widget, not on <body>: the cart drawer is a modal <dialog>, and
     everything outside a modal dialog is inert, so a textarea on <body> could
     not be focused or selected. */
  function legacyCopy(text, host) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:absolute;left:-9999px;top:0;opacity:0;';
    host.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    var ok = false;
    try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
    host.removeChild(ta);
    return ok;
  }

  function copied(btn, coupon, code, ok) {
    var label = btn.querySelector('[data-ms-copy-label]');
    var live = coupon.querySelector('[data-ms-live]');
    if (ok) {
      btn.classList.add('is-copied');
      if (label) label.textContent = 'Copied';
      if (live) live.textContent = code + ' copied. Paste it at checkout.';
      coupon.classList.remove('is-pulse');
      void coupon.offsetWidth;
      coupon.classList.add('is-pulse');
    } else {
      /* Could not reach the clipboard — select the code so a long-press copy
         works, and say so rather than claiming success. */
      var codeEl = coupon.querySelector('[data-ms-code]');
      if (codeEl && window.getSelection) {
        var range = document.createRange();
        range.selectNodeContents(codeEl);
        var sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
      if (label) label.textContent = 'Press & copy';
      if (live) live.textContent = 'Code selected. Copy it and paste at checkout.';
    }
    clearTimeout(resetTimer);
    resetTimer = setTimeout(function () { resetCopied(btn); }, 2400);
  }

  /* Delegated: the drawer replaces this markup on every cart change, so a
     listener bound to the button itself would be lost on the first update. */
  document.addEventListener('click', function (event) {
    var btn = event.target && event.target.closest ? event.target.closest('[data-ms-copy]') : null;
    if (!btn) return;
    var coupon = btn.closest('[data-ms-coupon]');
    var codeEl = coupon && coupon.querySelector('[data-ms-code]');
    var code = codeEl ? codeEl.textContent.trim() : '';
    if (!code) return;
    event.preventDefault();

    try {
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(code).then(
          function () { copied(btn, coupon, code, true); },
          function () { copied(btn, coupon, code, legacyCopy(code, coupon)); }
        );
      } else {
        copied(btn, coupon, code, legacyCopy(code, coupon));
      }
    } catch (e) {
      copied(btn, coupon, code, false);
    }
  });

  /* The theme dispatches cart:update on every quantity change, add and remove;
     maahi-cart-recs.js listens to the same event. */
  document.addEventListener('cart:update', refresh);
})();
